<template>
  <div :class="[$options.name, classList]" @click="$emit('click')">
    <span v-if="number && !loading">
      {{ innerNumber }}
    </span>
    <EdroneAvatarCircle v-else-if="avatarShape === '4' || loading" :color="colorCode" :size="sizeCode" />
    <EdroneAvatarSquare v-else-if="avatarShape === '2'" :color="colorCode" :size="sizeCode" />
    <EdroneAvatarRhomb v-else-if="avatarShape === '3'" :color="colorCode" :size="sizeCode" />
    <EdroneAvatarDashes v-else-if="avatarShape === '1'" :color="colorCode" :size="sizeCode" />
    <EdroneAvatarN v-else-if="avatarShape === '5'" :color="colorCode" :size="sizeCode" />
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, ref, watch, PropType } from "vue";
import EdroneAvatarCircle from "edrone-components/avatar/EdroneAvatarCircle.vue";
import EdroneAvatarN from "edrone-components/avatar/EdroneAvatarNShape.vue";
import EdroneAvatarSquare from "edrone-components/avatar/EdroneAvatarSquare.vue";
import EdroneAvatarRhomb from "edrone-components/avatar/EdroneAvatarRhomb.vue";
import EdroneAvatarDashes from "edrone-components/avatar/EdroneAvatarDashes.vue";

enum AvatarShape {
  Dashes = "1",
  Square = "2",
  Rhomb = "3",
  Circle = "4",
  N = "5",
}

enum AvatarColor {
  Gray = "gray",
  Blue = "blue",
  Green = "green",
  Violet = "violet",
  Orange = "orange",
}

export default defineComponent({
  name: "EdroneAvatar",
  components: {
    EdroneAvatarCircle,
    EdroneAvatarN,
    EdroneAvatarSquare,
    EdroneAvatarRhomb,
    EdroneAvatarDashes,
  },
  props: {
    /**
     * Customer ID required to generate the avatar, shape and color.
     */
    customerId: {
      type: Number,
      required: true,
      default: 100,
    },
    size: {
      type: String as PropType<"small" | "medium" | "big">,
      default: "medium",
    },
    number: {
      type: Number,
      default: 0,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    /**
     * Emits when the avatar is clicked.
     */
    "click",
  ],
  setup(props) {
    const internalCustomer = ref(props.customerId);
    const innerNumber = computed(() => {
      if (props.number) {
        return Math.abs(props.number) > 99 ? 99 : Math.abs(props.number);
      }
      return 0;
    });

    const avatarColor = computed(() => {
      const color = Math.floor((internalCustomer.value - 1) / 5);
      const colors = Object.values(AvatarColor);
      return colors[color % colors.length] || AvatarColor.Gray;
    });

    const avatarShape = computed(() => {
      const shape = Math.floor(internalCustomer.value - 1);
      const shapes = Object.values(AvatarShape);

      return shapes[shape % shapes.length] || AvatarShape.Circle;
    });

    watch(
      () => props.customerId,
      (value) => {
        internalCustomer.value = Math.abs(value);
      }
    );

    const colorCode = computed(() => {
      if (props.loading) {
        return "#BFBFBF";
      }
      switch (avatarColor.value) {
        case "blue":
          return "#1A7DFF";
        case "green":
          return "#00D756";
        case "violet":
          return "#542CFA";
        case "orange":
          return "#FF6206";
        case "gray":
        default:
          return "#545454";
      }
    });
    const sizeCode = computed(() => {
      switch (props.size) {
        case "small":
          return 20;
        case "big":
          return 40;
        case "medium":
        default:
          return 32;
      }
    });

    const classList = computed(() => {
      return {
        [`EdroneAvatar--${props.size}`]: true,
        [`EdroneAvatar--loading`]: props.loading,
        [`EdroneAvatar--number`]: props.number,
        [`EdroneAvatar--${avatarColor.value}`]: true,
      };
    });

    return {
      avatarColor,
      avatarShape,
      internalCustomer,
      innerNumber,
      classList,
      colorCode,
      sizeCode,
    };
  },
});
</script>

<style scoped>
@reference "vue-app/assets/scss/main.css";

.EdroneAvatar {
  width: 32px;
  height: 32px;
  min-width: 32px;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;

  font-family: Roboto, serif;
  font-style: normal;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -0.56px;
}

.EdroneAvatar {
  @apply tw:text-default-700 tw:bg-default-100 tw:border tw:border-default-200;
}

.EdroneAvatar--blue {
  @apply tw:bg-blue-000 tw:border-blue-100;
}

.EdroneAvatar--orange {
  @apply tw:bg-orange-000 tw:border-orange-100;
}

.EdroneAvatar--green {
  @apply tw:bg-green-000 tw:border-green-100;
}

.EdroneAvatar--violet {
  @apply tw:bg-violet-000 tw:border-violet-100;
}

.EdroneAvatar--gray,
.EdroneAvatar--loading,
.EdroneAvatar--number {
  @apply tw:bg-default-100 tw:border-default-200;
}

.EdroneAvatar--small {
  width: 20px;
  height: 20px;
  min-width: 20px;
  font-size: 12px;
}

.EdroneAvatar--big {
  width: 40px;
  height: 40px;
  min-width: 40px;
  font-size: 16px;
}

.EdroneAvatar--medium {
  width: 32px;
  height: 32px;
  min-width: 32px;
  font-size: 14px;
}
</style>
